import { isIchOrganization } from "../constants/ichOrganizations";
import _ from "lodash";
import catalog from "../constants/ichMilestoneCatalog.json";

/**
 * ICH (Infinity Custom Homes) milestone model.
 *
 * The catalog in src/constants/ichMilestoneCatalog.json is generated from ICH's
 * "Customer Process Tracker" workbook plus the platform workflow-state
 * definition (see src/mockData/projects/ich/build_ich_milestone_catalog.py).
 * One row of that workbook is one house, and one house is one structural
 * "contract anchor" activity instance in AmbiFi. This module keeps every
 * milestone value in three places on that instance, following the pattern the
 * platform already uses for job flags and due dates:
 *
 *   1. the activity item's value in the instance tree (item id "milestone_<key>")
 *   2. instance.variables["milestone_<key>"]
 *   3. a root-level mirror instance["ichMilestone_<key>"]
 *
 * The root mirror is what in-app code reads first (cheap, no tree walk). The
 * Algolia history indexer (ambifi-search-index-lambda, develop branch) does not
 * copy arbitrary root fields; it copies the value of every item flagged
 * isSearchable as "<item id>" (dates as epoch seconds at noon UTC) plus
 * "<item id>IsoString". The generated Operations items are flagged searchable,
 * so a hit carries milestone_<key> / milestone_<key>IsoString and the Homes
 * dashboard can read milestones without loading the instance JSON from S3.
 * Instances created before the Operations list existed carry only the mirrors,
 * and the dashboard hydrates those rows from S3.
 */

export const ICH_ORG_ID = "a25f9e42-23cd-11ec-9068-066917b38554";

export const ICH_ACTIVITY_KEYS = Object.freeze({
	structural: "7f1468c1-cc9c-4e44-8b80-9493b545e459",
	plumbing: "c1031d69-7550-4b3b-9541-7b3bec97c3be"
});

export const ICH_MILESTONE_ITEM_ID_PREFIX = "milestone_";
export const ICH_MILESTONE_ROOT_PREFIX = "ichMilestone_";
export const ICH_PHASE_ROOT_FIELDS = Object.freeze({
	phase: "ichPhase",
	phaseName: "ichPhaseName",
	currentMilestone: "ichCurrentMilestone",
	currentMilestoneLabel: "ichCurrentMilestoneLabel",
	nextMilestone: "ichNextMilestone",
	nextMilestoneLabel: "ichNextMilestoneLabel",
	nextMilestoneDue: "ichNextMilestoneDue",
	nextMilestoneDueIsoString: "ichNextMilestoneDueIsoString"
});

export const ICH_CHECK_VALUES = Object.freeze({
	DONE: "done",
	NOT_APPLICABLE: "na",
	UNKNOWN: "unknown",
	PENDING: ""
});

export const ICH_MILESTONE_KINDS = Object.freeze({
	INFO: "info",
	CHECK: "check",
	DATE: "date",
	DERIVED: "derived",
	NUMBER: "number",
	TEXT: "text"
});

export const ICH_MILESTONE_EVENTS = Object.freeze({
	AGREEMENT_OF_SALE_LOCKED: "agreementOfSaleLocked",
	ADDENDUM_LOCKED: "addendumLocked",
	SESSION_SUBMITTED: "sessionSubmitted"
});

/**
 * Status before anything is reached. Mirrored in
 * src/scripts/ich/buildIchWorkflowStates.js (a jest test guards the two).
 */
export const ICH_PRE_CONTRACT_PHASE = Object.freeze({
	id: "preContract",
	name: "Pre-Contract",
	subStateId: "structuralSelectionsInProgress"
});

const DUE_SOON_DAYS = 7;

const CHECK_DONE_TOKENS = new Set(["done", "xx", "x", "yes", "y", "true", "complete", "completed", "1"]);
const CHECK_NA_TOKENS = new Set(["na", "n/a", "not applicable", "notapplicable"]);
const CHECK_UNKNOWN_TOKENS = new Set(["unknown", "??", "?", "wip", "tbd", "pending"]);

const sortedMilestones = _.sortBy(catalog.milestones, "order");
const milestoneByKey = _.keyBy(sortedMilestones, "key");
const phaseGroupIds = catalog.groups.filter((group) => group.type === "phase").map((group) => group.id);
const groupById = _.keyBy(catalog.groups, "id");
// Phase milestones in status order: by phase, then tracker order within the phase.
// Platform-only milestones sit after every tracker column in global order but
// belong to an earlier phase, so global order alone would let the phase regress.
const phaseSequence = phaseGroupIds.reduce((sequence, groupId) => sequence.concat(
	sortedMilestones.filter((milestone) => (
		milestone.group === groupId
		&& (milestone.kind === ICH_MILESTONE_KINDS.CHECK || milestone.kind === ICH_MILESTONE_KINDS.DATE)
	))
), []);

export function getIchMilestoneCatalog() {
	return catalog;
}

export function getIchMilestoneGroups() {
	return catalog.groups;
}

/**
 * The status groups a home moves through: Pre-Contract, then the catalog's
 * phase groups in order. Same ids and order as the generated workflow states.
 */
export function getIchPhases() {
	return [{ id: ICH_PRE_CONTRACT_PHASE.id, name: ICH_PRE_CONTRACT_PHASE.name, type: "phase" }]
		.concat(catalog.groups.filter((group) => group.type === "phase"));
}

export function getIchMilestoneRoles() {
	return catalog.roles;
}

export function getIchMilestones(filter = {}) {
	const kinds = filter.kinds ? new Set(filter.kinds) : null;
	return sortedMilestones.filter((milestone) => {
		if (kinds && !kinds.has(milestone.kind)) {
			return false;
		}
		if (filter.group && milestone.group !== filter.group) {
			return false;
		}
		if (filter.role && milestone.role !== filter.role) {
			return false;
		}
		return true;
	});
}

export function getIchTrackedMilestones() {
	return getIchMilestones({ kinds: [ICH_MILESTONE_KINDS.CHECK, ICH_MILESTONE_KINDS.DATE] });
}

export function getIchMilestoneByKey(key) {
	return milestoneByKey[key] || null;
}

export function getIchMilestoneItemId(key) {
	return `${ICH_MILESTONE_ITEM_ID_PREFIX}${key}`;
}

export function getIchMilestoneRootField(key) {
	return `${ICH_MILESTONE_ROOT_PREFIX}${key}`;
}

export function getIchActivityKey(activityId = "", activityName = "") {
	const id = String(activityId || "").trim().toLowerCase();
	const match = Object.keys(ICH_ACTIVITY_KEYS).find((key) => ICH_ACTIVITY_KEYS[key].toLowerCase() === id);
	if (match) {
		return match;
	}
	const name = String(activityName || "").toLowerCase();
	if (name.includes("plumbing")) {
		return "plumbing";
	}
	if (name.includes("appliance")) {
		return "appliances";
	}
	if (name.includes("structural")) {
		return "structural";
	}
	return null;
}

function pad2(value) {
	return String(value).padStart(2, "0");
}

function toIsoDate(date) {
	return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function normalizeIchCheckValue(raw) {
	if (raw === true) {
		return ICH_CHECK_VALUES.DONE;
	}
	if (raw === false || raw === null || raw === undefined) {
		return ICH_CHECK_VALUES.PENDING;
	}
	if (_.isPlainObject(raw)) {
		return normalizeIchCheckValue(raw.value);
	}
	const token = String(raw).trim().toLowerCase();
	if (!token) {
		return ICH_CHECK_VALUES.PENDING;
	}
	if (CHECK_DONE_TOKENS.has(token)) {
		return ICH_CHECK_VALUES.DONE;
	}
	if (CHECK_NA_TOKENS.has(token)) {
		return ICH_CHECK_VALUES.NOT_APPLICABLE;
	}
	if (CHECK_UNKNOWN_TOKENS.has(token)) {
		return ICH_CHECK_VALUES.UNKNOWN;
	}
	return ICH_CHECK_VALUES.DONE;
}

export function normalizeIchDateValue(raw) {
	if (raw === null || raw === undefined || raw === "" || raw === false) {
		return "";
	}
	if (_.isPlainObject(raw)) {
		return normalizeIchDateValue(raw.value);
	}
	if (raw instanceof Date) {
		return Number.isNaN(raw.getTime()) ? "" : toIsoDate(raw);
	}
	if (typeof raw === "number" && Number.isFinite(raw)) {
		const millis = raw < 1e11 ? raw * 1000 : raw;
		return toIsoDate(new Date(millis));
	}
	const text = String(raw).trim();
	const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (iso) {
		return `${iso[1]}-${iso[2]}-${iso[3]}`;
	}
	const us = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
	if (us) {
		const year = us[3].length === 2 ? `20${us[3]}` : us[3];
		return `${year}-${pad2(us[1])}-${pad2(us[2])}`;
	}
	const parsed = new Date(text);
	return Number.isNaN(parsed.getTime()) ? "" : toIsoDate(parsed);
}

export function normalizeIchMilestoneValue(milestone, raw) {
	if (!milestone) {
		return raw;
	}
	if (milestone.kind === ICH_MILESTONE_KINDS.DATE) {
		return normalizeIchDateValue(raw);
	}
	if (milestone.kind === ICH_MILESTONE_KINDS.CHECK) {
		return normalizeIchCheckValue(raw);
	}
	if (_.isPlainObject(raw)) {
		return raw.value;
	}
	return raw;
}

function findTreeItemValue(node, itemId) {
	if (!node || typeof node !== "object") {
		return undefined;
	}
	if (node.id === itemId && Object.prototype.hasOwnProperty.call(node, "value")) {
		return node.value;
	}
	const children = Array.isArray(node.children) ? node.children : [];
	for (let i = 0; i < children.length; i += 1) {
		const found = findTreeItemValue(children[i], itemId);
		if (found !== undefined) {
			return found;
		}
	}
	return undefined;
}

function setTreeItemValue(node, itemId, value) {
	if (!node || typeof node !== "object") {
		return false;
	}
	let updated = false;
	if (node.id === itemId) {
		node.value = value;
		updated = true;
	}
	const children = Array.isArray(node.children) ? node.children : [];
	for (let i = 0; i < children.length; i += 1) {
		if (setTreeItemValue(children[i], itemId, value)) {
			updated = true;
		}
	}
	return updated;
}

export function readIchMilestoneValue(instance, key) {
	const milestone = getIchMilestoneByKey(key);
	if (!instance || !milestone) {
		return milestone && milestone.kind === ICH_MILESTONE_KINDS.CHECK ? ICH_CHECK_VALUES.PENDING : "";
	}
	const rootField = getIchMilestoneRootField(key);
	if (instance[rootField] !== undefined && instance[rootField] !== null) {
		return normalizeIchMilestoneValue(milestone, instance[rootField]);
	}
	const itemId = getIchMilestoneItemId(key);
	const variables = _.isPlainObject(instance.variables) ? instance.variables : {};
	if (variables[itemId] !== undefined) {
		return normalizeIchMilestoneValue(milestone, variables[itemId]);
	}
	const treeValue = findTreeItemValue(instance, itemId);
	if (treeValue !== undefined) {
		return normalizeIchMilestoneValue(milestone, treeValue);
	}
	return normalizeIchMilestoneValue(milestone, undefined);
}

export function readIchMilestoneValues(instance) {
	const values = {};
	getIchTrackedMilestones().forEach((milestone) => {
		values[milestone.key] = readIchMilestoneValue(instance, milestone.key);
	});
	return values;
}

export function writeIchMilestoneValue(instance, key, rawValue) {
	const milestone = getIchMilestoneByKey(key);
	if (!milestone || !instance) {
		return instance;
	}
	const value = normalizeIchMilestoneValue(milestone, rawValue);
	const itemId = getIchMilestoneItemId(key);
	const next = _.cloneDeep(instance);
	setTreeItemValue(next, itemId, value);
	const variables = _.isPlainObject(next.variables) ? next.variables : {};
	if (_.isPlainObject(variables[itemId])) {
		variables[itemId] = { ...variables[itemId], value };
	} else {
		variables[itemId] = value;
	}
	next.variables = variables;
	next[getIchMilestoneRootField(key)] = value;
	return next;
}

export function isIchMilestoneReached(milestone, value) {
	if (!milestone) {
		return false;
	}
	if (milestone.kind === ICH_MILESTONE_KINDS.DATE) {
		return Boolean(normalizeIchDateValue(value));
	}
	if (milestone.kind === ICH_MILESTONE_KINDS.CHECK) {
		return normalizeIchCheckValue(value) === ICH_CHECK_VALUES.DONE;
	}
	return false;
}

export function computeIchTargetDate(contractDate, milestone) {
	const start = normalizeIchDateValue(contractDate);
	if (!start || !milestone || !Number.isFinite(milestone.targetDays)) {
		return null;
	}
	const [year, month, day] = start.split("-").map(Number);
	const target = new Date(year, month - 1, day + milestone.targetDays);
	return toIsoDate(target);
}

export function getIchMilestoneSignal(milestone, values = {}, today = new Date()) {
	if (!milestone) {
		return { state: "pending", targetDate: null };
	}
	const value = values[milestone.key];
	const targetDate = computeIchTargetDate(values.contractDate, milestone);
	if (milestone.kind === ICH_MILESTONE_KINDS.CHECK) {
		const check = normalizeIchCheckValue(value);
		if (check === ICH_CHECK_VALUES.DONE) {
			return { state: "done", targetDate };
		}
		if (check === ICH_CHECK_VALUES.NOT_APPLICABLE) {
			return { state: "na", targetDate };
		}
		if (check === ICH_CHECK_VALUES.UNKNOWN) {
			return { state: "unknown", targetDate };
		}
	} else if (milestone.kind === ICH_MILESTONE_KINDS.DATE && normalizeIchDateValue(value)) {
		return { state: "done", targetDate };
	}
	if (!targetDate) {
		return { state: "pending", targetDate: null };
	}
	const todayIso = normalizeIchDateValue(today);
	if (targetDate < todayIso) {
		return { state: "overdue", targetDate };
	}
	const [year, month, day] = todayIso.split("-").map(Number);
	const soonIso = toIsoDate(new Date(year, month - 1, day + DUE_SOON_DAYS));
	if (targetDate <= soonIso) {
		return { state: "dueSoon", targetDate };
	}
	return { state: "pending", targetDate };
}

/**
 * Reads the tracker the way a person does: find the leftmost open column.
 *
 *   - phase: Pre-Contract until Contract Date is reached; then the earliest phase
 *     that still has a pending milestone (mark a column N/A to move past it);
 *     the last phase once nothing is pending. Work done out of order (plumbing
 *     selections before lot closing) does not push the home forward.
 *   - nextMilestone: the earliest pending milestone overall, with its target date.
 *   - currentMilestone: the last milestone reached within the phase, falling back
 *     to the last one reached anywhere. This is the sub-status headline.
 */
export function deriveIchPhase(values = {}) {
	const reached = (milestone) => isIchMilestoneReached(milestone, values[milestone.key]);
	const pending = (milestone) => !reached(milestone) && normalizeIchCheckValue(values[milestone.key]) !== ICH_CHECK_VALUES.NOT_APPLICABLE;
	const next = phaseSequence.find(pending) || null;
	const reachedMilestones = phaseSequence.filter(reached);
	const lastReached = reachedMilestones.length ? reachedMilestones[reachedMilestones.length - 1] : null;

	let phaseId;
	if (!reached(milestoneByKey.contractDate)) {
		phaseId = ICH_PRE_CONTRACT_PHASE.id;
	} else if (next) {
		phaseId = next.group;
	} else {
		phaseId = phaseGroupIds[phaseGroupIds.length - 1];
	}
	const phase = phaseId === ICH_PRE_CONTRACT_PHASE.id ? ICH_PRE_CONTRACT_PHASE : groupById[phaseId] || null;
	const reachedInPhase = reachedMilestones.filter((milestone) => milestone.group === phaseId);
	const current = reachedInPhase.length ? reachedInPhase[reachedInPhase.length - 1] : lastReached;
	return {
		phaseId,
		phaseName: phase ? phase.name : "",
		currentMilestone: current,
		nextMilestone: next,
		nextMilestoneTargetDate: next ? computeIchTargetDate(values.contractDate, next) : null
	};
}

/**
 * Status = phase, sub-status = the last milestone reached in that phase (its
 * key is the sub-state id in the generated workflow states). Both are derived;
 * a status set by hand is overwritten on the next stamp or edit.
 */
export function buildIchWorkflowUpdate(values = {}) {
	const derived = deriveIchPhase(values);
	const current = derived.currentMilestone;
	return {
		workflowStatus: derived.phaseId,
		workflowSubStatus: current ? current.key : ICH_PRE_CONTRACT_PHASE.subStateId
	};
}

export function computeIchRootMirrors(values = {}) {
	const mirrors = {};
	getIchTrackedMilestones().forEach((milestone) => {
		mirrors[getIchMilestoneRootField(milestone.key)] = values[milestone.key] === undefined ? "" : values[milestone.key];
	});
	const derived = deriveIchPhase(values);
	mirrors[ICH_PHASE_ROOT_FIELDS.phase] = derived.phaseId;
	mirrors[ICH_PHASE_ROOT_FIELDS.phaseName] = derived.phaseName;
	mirrors[ICH_PHASE_ROOT_FIELDS.currentMilestone] = derived.currentMilestone ? derived.currentMilestone.key : "";
	mirrors[ICH_PHASE_ROOT_FIELDS.currentMilestoneLabel] = derived.currentMilestone ? derived.currentMilestone.label : "";
	mirrors[ICH_PHASE_ROOT_FIELDS.nextMilestone] = derived.nextMilestone ? derived.nextMilestone.key : "";
	mirrors[ICH_PHASE_ROOT_FIELDS.nextMilestoneLabel] = derived.nextMilestone ? derived.nextMilestone.label : "";
	mirrors[ICH_PHASE_ROOT_FIELDS.nextMilestoneDueIsoString] = derived.nextMilestoneTargetDate || "";
	mirrors[ICH_PHASE_ROOT_FIELDS.nextMilestoneDue] = derived.nextMilestoneTargetDate
		? Math.floor(new Date(`${derived.nextMilestoneTargetDate}T00:00:00`).getTime() / 1000)
		: null;
	return mirrors;
}

export function getIchMilestonesForEvent(event = {}) {
	const type = event.type || "";
	const suffix = event.number !== undefined && event.number !== null
		? String(event.number)
		: (event.activityKey || "");
	const source = suffix ? `${type}:${suffix}` : type;
	return getIchTrackedMilestones().filter((milestone) => milestone.autoSource === source);
}

export function applyIchMilestoneMirrors(instance) {
	if (!instance) {
		return instance;
	}
	const values = readIchMilestoneValues(instance);
	return { ...instance, ...computeIchRootMirrors(values) };
}

/**
 * Stamps every milestone the platform can prove from an event, then refreshes
 * the root mirrors and the workflow status/sub-status so boards and filters
 * follow automatically. Returns a new instance; never mutates the input.
 *
 * event: { type: ICH_MILESTONE_EVENTS.*, number?: 1|2, activityKey?: "plumbing", at?: Date }
 */
export function stampIchMilestones(instance, event = {}) {
	if (!instance) {
		return instance;
	}
	const milestones = getIchMilestonesForEvent(event);
	if (!milestones.length) {
		return instance;
	}
	const at = normalizeIchDateValue(event.at || new Date());
	let next = instance;
	milestones.forEach((milestone) => {
		const existing = readIchMilestoneValue(next, milestone.key);
		if (isIchMilestoneReached(milestone, existing)) {
			return;
		}
		const value = milestone.kind === ICH_MILESTONE_KINDS.DATE ? at : ICH_CHECK_VALUES.DONE;
		next = writeIchMilestoneValue(next, milestone.key, value);
	});
	const values = readIchMilestoneValues(next);
	const workflow = buildIchWorkflowUpdate(values);
	return {
		...next,
		...computeIchRootMirrors(values),
		workflowStatus: workflow.workflowStatus,
		workflowSubStatus: workflow.workflowSubStatus
	};
}

export function isIchOrg(orgId = "") {
	return isIchOrganization(orgId);
}
